import { call, spawn } from 'typed-redux-saga'
import url from 'url'

import Config from 'APP/Config'
import { setIsConnectedIfDisconnected } from 'APP/Sagas/AppSessionSagas'
import type { MattermostClient } from 'APP/Services/Mattermost'
import type { ActionItem, RawPostList } from 'APP/Services/Mattermost/types'
import type { Channel } from 'APP/Store/History/types'

import { DIALOGUE_SYSTEM_TYPE, LEGACY_DIALOGUE_SYSTEM_TYPE } from './constants'
import { formatPostMessage } from './legacyUtils'
import type {
  EnrichedPost,
  NormalizedPost,
  NormalizedPostList,
  NormalizedPostListOrError,
} from './types'

type FetchNewPostsRequestHelperArgs = {
  channel: Channel
  channelId: string
  forceFetchAllPosts?: boolean
  limit?: number
  mmClient: MattermostClient
}

export function* fetchNewPostsRequestHelper({
  channelId,
  limit = 1000,
  forceFetchAllPosts = false,
  mmClient,
  channel,
}: FetchNewPostsRequestHelperArgs): Generator<any, NormalizedPostListOrError, any> {
  try {
    const { lastFetchedPostAt } = channel
    const postData =
      !lastFetchedPostAt || forceFetchAllPosts
        ? yield* call(mmClient.getPosts, channelId, { limit: forceFetchAllPosts ? 1000 : limit })
        : yield* call(mmClient.getPostsSince, channelId, lastFetchedPostAt)

    yield* spawn(setIsConnectedIfDisconnected)

    return postData
  } catch (error) {
    return { error, posts: {}, order: [] }
  }
}

export const isValidPostData = (
  postData: NormalizedPostListOrError | undefined
): postData is NormalizedPostList => {
  return !!(
    postData &&
    postData.order &&
    postData.order.length &&
    postData.posts &&
    Object.keys(postData.posts).length
  )
}

// This is unfortunately partially duplicated by getFormattedLastPost, in the Episode List helpers.
// TODO: consolidate code that handles "last post" from ER episodes and posts obtained through our
// direct Mattermost connection (fetch/WebSocket).
export const normalizePostActions = (rawPost: NormalizedPost): EnrichedPost => {
  let post: EnrichedPost = { ...rawPost, actionType: 'none' }
  let action: ActionItem | Partial<ActionItem> = {}

  // TODO: Remove LEGACY_DIALOGUE_SYSTEM_TYPE once it's completely deprecated
  if (
    (post.type === LEGACY_DIALOGUE_SYSTEM_TYPE || post.type === DIALOGUE_SYSTEM_TYPE) &&
    post.props
  ) {
    post.actionType = post.props.type
    post.actionContext = { ...post.props }
  }

  if (post.props && post.props.actions && post.props.actions[0]) {
    action = post.props.actions[0]
  }

  // Parses embedded actions formatted as %[Text](ActionURI)
  // messageParts[1] = Text, messageParts[2] = URI
  if (post.message && /%\[.*?\]\(.*?\)/.test(post.message)) {
    const messageParts = post.message.match(/%\[(.*?)\]\((.*?)\)/)
    action = {
      uri: messageParts?.[2],
      text: messageParts?.[1],
    }
  }

  if (action && 'uri' in action && action.uri) {
    const uri = action.uri
    const uriComponents = uri.split('/')
    if (uri.indexOf('/start-encounter/') > -1) {
      post.actionType = 'videoCall'
      post.actionContext = { encounterId: uriComponents[uriComponents.length - 1] }
    }
    if (uri.indexOf('/encounter/') > -1) {
      post.actionType = 'carePlan'
      post.actionContext = { encounterId: uriComponents[uriComponents.length - 1] }
    }
    if (uri.indexOf('/create-child') > -1) {
      post.actionType = 'createChild'
      post.actionContext = { text: action.text }
    }
    if (uri.indexOf('/photo-id') > -1) {
      post.actionType = 'takeIdPhoto'
      post.actionContext = { text: action.text }
    }
    if (uri.indexOf('/charge') > -1) {
      post.actionType = 'charge'
      const params: Record<string, string> = {}
      uri
        .split('?')[1]
        .split('&')
        .map((s) => s.split('='))
        .forEach((item) => {
          params[item[0]] = item[1]
        })
      post.actionContext = {
        text: action.text,
        amount: params.amt,
        description: params.desc,
        uuid: params.uuid,
      }
    }
  }
  post.message = formatPostMessage(post) || ''
  return post
}

// Used strictly for debugging purposes through debug menu
export let disableRequests = false
export const toggleDisableExtraPropsSourceRequests = () => (disableRequests = !disableRequests)

const AUTH_ALLOWLIST = Config.EXTRA_PROPS_SOURCE_AUTH_ALLOWLIST || []
export const urlMatchesAuthWhitelist = (requestUrl: string): boolean => {
  const { hostname } = url.parse(requestUrl) || {}
  return !!AUTH_ALLOWLIST.find((domain) => (hostname || '').endsWith(domain))
}

export const urlIsHttps = (requestUrl: string): boolean => requestUrl.indexOf('https') === 0

async function getExtraProps(requestUrl: string, authToken?: string | null): Promise<any> {
  const headers = {
    'Content-Type': 'application/json',
  }
  if (authToken && urlMatchesAuthWhitelist(requestUrl) && urlIsHttps(requestUrl)) {
    Object.assign(headers, { Authorization: `Bearer ${authToken}` })
  }

  const res = await fetch(requestUrl, { headers })
  if (!res.ok || disableRequests) {
    throw new Error(`Failed to resolve extra_props_source: ${requestUrl}`)
  } else {
    const extraProps = await res.json()
    return extraProps
  }
}

export async function resolveExtraProps(
  postData: Pick<RawPostList, 'order' | 'posts'>,
  authToken?: string | null
): Promise<NormalizedPostList> {
  let missingData = false
  const posts = postData.posts
  const postEntries = await Promise.all(
    Object.entries(posts).map(([id, post]) => {
      if (
        post &&
        post.props &&
        'extra_props_source' in post.props &&
        typeof post.props.extra_props_source === 'string'
      ) {
        return getExtraProps(post.props.extra_props_source, authToken)
          .then((extraProps) => [
            id,
            {
              ...post,
              props: {
                ...post.props,
                ...extraProps,
                extra_props_source: undefined,
                missingData: undefined,
                missingDataReason: undefined,
              },
            },
          ])
          .catch((err) => {
            missingData = true
            return [
              id,
              {
                ...post,
                props: {
                  ...post.props,
                  extra_props_source: undefined,
                  missingData: true,
                  missingDataReason: err,
                },
              },
            ]
          })
      } else {
        return Promise.resolve([id, post])
      }
    })
  )

  return {
    ...postData,
    posts: Object.fromEntries(postEntries),
    missingData,
  }
}
